import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { motion } from "framer-motion";
import styled from "styled-components";
import echoxborderLogo from "../EchoxborderLogo.png";
// import wisdomLogo from "../logo-wis.avif"; // Commented for future use

// Elegant corporate background with subtle pattern
const CorporateSidebar = styled.div`
  background: linear-gradient(135deg, #1A2654 0%, #2563eb 50%, #1e40af 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
    background-size: 40px 40px;
    background-position: 0 0, 20px 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.01) 50%,
      transparent 70%
    );
  }
`;

// Glass morphism form container
const GlassForm = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const Login = () => {
  const { currentUser, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const onCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!captchaValue) {
      setError("Please verify that you are not a robot.");
      setIsLoading(false);
      return;
    }

    login(email, password, captchaValue, (error) => {
      setIsLoading(false);
      if (error) {
        console.error("Login failed:", error);
        if (error.message === "You do not have access to this system") {
          setError(
            "You do not have access to this system. Please contact your administrator."
          );
        } else {
          setError("Invalid email or password.");
        }
      }
    });
  };

  useEffect(() => {
    if (currentUser?.accessToken) {
      navigate("/trafficControlTower");
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    // Set body background to match login page
    document.body.style.backgroundColor = '#f9fafb';
    return () => {
      // Reset body background when component unmounts
      document.body.style.backgroundColor = '';
    };
  }, []);

  // Simplified logo animation variants
  const logoVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.2
      }
    },
    hover: {
      scale: 1.02,
      transition: { duration: 0.2 }
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-gray-50" style={{ backgroundColor: '#f9fafb' }}>
      {/* Left column for form */}
      <div className="w-1/2 flex justify-center items-center p-8" style={{ backgroundColor: '#f9fafb' }}>
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <GlassForm>
            {error && (
              <motion.div
                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
                role="alert"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <strong className="font-bold">Error:</strong>
                <span className="block sm:inline"> {error}</span>
              </motion.div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full p-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  disabled={isLoading}
                />
              </div>
              <div className="mb-6">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full p-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  disabled={isLoading}
                />
              </div>
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={onCaptchaChange}
                className="my-4"
              />
              <motion.button
                type="submit"
                className={`w-full ${
                  isLoading ? "bg-blue-300" : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                } text-white font-bold py-3 px-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300 shadow-lg`}
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? "Logging in..." : "Login"}
              </motion.button>
            </form>
          </GlassForm>
        </motion.div>
      </div>

      {/* Right column with elegant corporate background */}
      <CorporateSidebar className="w-1/2 flex justify-center items-center">
        {/* Logo container */}
        <motion.div
          className="flex flex-col items-center space-y-8 z-10 relative"
          variants={logoVariants}
          initial="initial"
          animate="animate"
        >
          {/* EchoxBorder Logo */}
          <motion.div
            variants={logoVariants}
            whileHover="hover"
            className="relative"
          >
            <div className="absolute inset-0 bg-white/5 rounded-2xl shadow-lg" />
            <img
              src={echoxborderLogo}
              alt="EchoxBorder logo"
              className="h-20 object-contain drop-shadow-lg relative z-10"
            />
          </motion.div>
        </motion.div>

        {/* Subtle ambient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/5 via-transparent to-black/10 pointer-events-none" />
      </CorporateSidebar>
    </div>
  );
};

export default Login;
